// tog'a #3355/#3408 (2026-10-04): «AI agentga qog'ozga yozilgan ro'yxatni yuborsa — jadvalga o'tkazib, istalgan bo'limga yo'naltirish».
// 📷 tugmasi (AI oynasida) → rasm tanlanadi yoki suratga olinadi → brauzerda 1600 px ga kichraytiriladi → POST AIAgent/ImageToTable →
// tahrirlanadigan jadval: qog'ozda nima yozilgan, mos tovar (avtomatik / tanlash / qidirish), soni, narxi → bo'lim tanlanadi →
// o'sha bo'limning ODDIY formasi tovarlar bilan to'lib ochiladi (huquq, narx qoidalari, qoldiq tekshiruvi — formaning o'zida). AI o'zi yozmaydi.
import React, { useEffect, useRef, useState } from "react"
import { Alert, Button, InputNumber, Modal, Radio, Select, Space, Tag, Tooltip, message } from "antd"
import StickyTable from "../table/StickyTable"   // tog'a #4695/#4703: hamma jadvalda sarlavha va «Итого» qotadi
import { CameraOutlined, DeleteOutlined } from "@ant-design/icons"
import { useHistory } from "react-router-dom"
import i18n from "../../services/i18n"
import { request } from "../../utils/config"
import { AiPrefillTarget, setAiPrefill } from "../../utils/aiPrefill"
import { permSync } from "../../utils/permSync"   // tog'a #3487: bo'limlar rolga qarab

const t = (k: string, d: string) => i18n.t(k, d)
const err = (e: any) => e?.response?.data?.message || e?.response?.data?.Message || e?.message || String(e)

// tog'a #3485: supplierProductId — yetkazuvchi (SAV) katalogidagi tovar (hamkor/magazin «Заказ поставщику» uchun); stock/barCode unda yo'q
type Tovar = { productId: number; supplierProductId?: number; name: string; article?: string; barCode?: string; price: number | null; costPriceUSD?: number; discount?: number; stock?: number; score?: number }
type Moslik = { status: "ok" | "check" | "none"; match: Tovar | null; candidates: Tovar[] }
type Qator = { key: number; readName?: string; readArticle?: string; qty: number; price?: number | null; unit?: string; status: "ok" | "check" | "none"; match: Tovar | null; candidates: Tovar[]; supplier?: Moslik | null }
type Natija = { rows: Qator[]; counterparty?: string; currency?: string; hasSupplier?: boolean; target?: AiPrefillTarget | null }   // tog'a #3902: target — bo'lim ichida yuborilgan rasm qaysi formaga

/** Rasmni 1600 px gacha kichraytirib JPEG base64 qiladi (telefon rasmi 3–8 MB → ~0.3–0.8 MB) */
async function kichraytir(file: File): Promise<{ base64: string; mime: string }> {
  const url = URL.createObjectURL(file)
  try {
    const img: HTMLImageElement = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url })
    const k = Math.min(1, 1600 / Math.max(img.width, img.height))
    const c = document.createElement("canvas"); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k)
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height)
    const data = c.toDataURL("image/jpeg", 0.82)
    return { base64: data.split(",")[1], mime: "image/jpeg" }
  } finally { URL.revokeObjectURL(url) }
}

export function AiPhotoButton({ disabled, onBusy, onDone, section }: { disabled?: boolean; onBusy?: (b: boolean) => void; onDone?: () => void; section?: string | null }) {
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [natija, setNatija] = useState<Natija | null>(null)
  const tanla = async (f?: File) => {
    if (!f) return
    setBusy(true); onBusy?.(true)
    try {
      const img = await kichraytir(f)
      const r = (await request({ url: "AIAgent/ImageToTable", method: "post", data: { imageBase64: img.base64, mime: img.mime, section: section || undefined } })).data   // tog'a #3902
      if (!r?.rows?.length) { message.warning(r?.message || t("aiImgNone", "Rasmda tovar ro'yxati topilmadi")); return }
      setNatija({ rows: r.rows, counterparty: r.counterparty, currency: r.currency, hasSupplier: !!r.hasSupplier, target: r.target || null })
    } catch (e: any) { message.error(err(e)) }
    finally { setBusy(false); onBusy?.(false); if (ref.current) ref.current.value = "" }
  }
  return (
    <>
      <Tooltip title={t("aiImgTip", "Qog'ozdagi ro'yxat rasmini yuboring — AI jadvalga aylantiradi")}>
        <Button shape="circle" type="text" icon={<CameraOutlined />} loading={busy} disabled={disabled || busy} onClick={() => ref.current?.click()} data-testid="ai-photo" aria-label="📷" />
      </Tooltip>
      <input ref={ref} type="file" accept="image/*" capture="environment" style={{ display: "none" }} data-testid="ai-photo-input" onChange={(e) => tanla(e.target.files?.[0])} />
      {natija && <AiListImportModal data={natija} onClose={() => setNatija(null)} onDone={onDone} />}
    </>
  )
}

/** Qator uchun tovar qidirish. O'z katalogi: Products?SearchText; yetkazuvchi katalogi (hamkor «Заказ поставщику»): Orders/OrderCatalog */
function TovarQidir({ onPick, supplier }: { onPick: (p: Tovar) => void; supplier?: boolean }) {
  const [opts, setOpts] = useState<Tovar[]>([])
  const [loading, setLoading] = useState(false)
  const timer = useRef<any>(null)
  const qidir = (q: string) => {
    clearTimeout(timer.current)
    if (!q || q.trim().length < 2) { setOpts([]); return }
    timer.current = setTimeout(async () => {
      setLoading(true)
      try {
        if (supplier) {
          const d = (await request({ url: "Orders/OrderCatalog", params: { source: "supplier", search: q.trim(), take: 20 } })).data
          setOpts((Array.isArray(d?.data) ? d.data : []).map((p: any) => ({ productId: p.supplierProductId, supplierProductId: p.supplierProductId, name: p.name, article: p.article, price: p.priceUSD ?? null })))
        } else {
          const d = (await request({ url: "Products", params: { PageNumber: 1, PageSize: 20, SearchText: q.trim() } })).data
          setOpts((Array.isArray(d?.data) ? d.data : []).map((p: any) => ({ productId: p.id, name: p.nameModel || p.name, article: p.article, barCode: p.barCode, price: Number(p.salePriceUSD || 0), costPriceUSD: Number(p.costPriceUSD || 0), discount: Number(p.discount || 0), stock: Number(p.totalCount ?? p.productCount ?? 0) })))
        }
      } catch { setOpts([]) } finally { setLoading(false) }
    }, 350)
  }
  return (
    <Select size="small" showSearch allowClear filterOption={false} loading={loading} value={undefined} style={{ width: "100%" }}
      placeholder={"🔍 " + t("aiImgSearch", "boshqa tovar qidirish (nom, artikul, shtrix-kod)")} onSearch={qidir} data-testid="ai-list-search"
      notFoundContent={loading ? "…" : null}
      onChange={(id) => { const p = opts.find((x) => x.productId === id); if (p) onPick(p) }}
      options={opts.map((p) => ({ value: p.productId, label: `${p.name}${p.article ? " · " + p.article : ""}` }))} />
  )
}

// tog'a #3485: «Статус» tushunarsiz edi — ustun «Найден в базе?», qiymatlar belgili
const HOLAT: Record<string, { c: string; l: () => string }> = {
  ok: { c: "green", l: () => "✓ " + t("aiImgFoundOk", "найден") },
  check: { c: "gold", l: () => "? " + t("aiImgFoundCheck", "проверьте") },
  none: { c: "red", l: () => "✗ " + t("aiImgFoundNone", "нет в базе") },
}
// jonli sinov (#3355): guruhlangan sahifada forma ichki ro'yxat oynasida — «+» tugmasidagi kabi parametrlar kerak
// (sotuv/zakaz: counterpartyId=null, zakupka: documents=null), aks holda ro'yxat ochilib, forma ochilmasdi
const YOL: Record<AiPrefillTarget, string> = { sale: "/sales?counterpartyId=null&sale=add", purchase: "/purchases?documents=null&purchase=add", order: "/orders?counterpartyId=null&order=add", orderToMain: "/orderToMain" }
// tog'a #3487: bo'lim tugmasi faqat shu bo'limda YARATISH huquqi bo'lsa chiqadi (rol / menejer kartasi bo'yicha)
const BOLIMLAR: { v: AiPrefillTarget; perm: string; label: () => string; supplier?: boolean }[] = [
  { v: "purchase", perm: "purchases", label: () => t("purchases", "Закупка") },
  { v: "sale", perm: "sales", label: () => t("sale", "Продажа") },
  { v: "order", perm: "orders", label: () => t("orders", "Заказ") },   // hamkor/magazinda — «Заказ клиента» (tog'a #3902, BOLIM_NOMI)
  { v: "orderToMain", perm: "orderToMain", label: () => t("aiImgOrderSup", "Заказ поставщику"), supplier: true },
]
const supQator = (r: Qator): Qator => ({ ...r, status: r.supplier?.status || "none", match: r.supplier?.match || null, candidates: r.supplier?.candidates || [] })

export function AiListImportModal({ data, onClose, onDone }: { data: Natija; onClose: () => void; onDone?: () => void }) {
  const history = useHistory()
  const ruxsat = BOLIMLAR.filter((b) => permSync(b.perm).canCreate && (!b.supplier || data.hasSupplier))
  const [own, setOwn] = useState<Qator[]>(data.rows)
  const [sup, setSup] = useState<Qator[]>(() => data.rows.map(supQator))
  const [target, setTarget] = useState<AiPrefillTarget | undefined>(() => {
    // tog'a #3902: bo'lim ichida yuborilgan rasm — o'sha bo'lim formasi
    const bolimdan = data.target ? ruxsat.find((x) => x.v === data.target) : undefined
    if (bolimdan) return bolimdan.v
    // tog'a #3902 (ADOLAT): hamkor/magazinda «zakaz» = YETKAZUVCHIGA — «Заказ поставщику» birinchi. Avval faqat o'z katalogida HECH NARSA
    // topilmasa tanlanardi; magazin katalogi SAV artikullari bilan bir xil (45/45) — har doim o'z skladi tanlanib qolardi.
    const b = (data.hasSupplier && ruxsat.find((x) => x.supplier)) || ruxsat[0]
    return b?.v
  })
  const supRejim = !!BOLIMLAR.find((b) => b.v === target)?.supplier
  const rows = supRejim ? sup : own
  const setRows = supRejim ? setSup : setOwn
  const yangila = (key: number, patch: Partial<Qator>) => setRows((p) => p.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  // soni / narx / o'chirish — ikkala ro'yxatda bir xil (bo'lim almashtirilganda yo'qolmasin)
  const ikkalasi = (f: (p: Qator[]) => Qator[]) => { setOwn(f); setSup(f) }
  const tayyor = rows.filter((r) => r.match && r.qty > 0)
  // tog'a #3413 (2026-10-04): telefonda jadval siqilib, ustunlar yon tomonga ketardi — tor ekranda har qator alohida karta.
  // tog'a #3504: iPad (820 px) da ham 7 ustun sig'masdi — «Кол-во» va narx ekrandan chiqib ketardi; 1000 px dan tor — kartalar.
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 1000)
  useEffect(() => { const on = () => setNarrow(window.innerWidth < 1000); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on) }, [])
  const holatTag = (r: Qator) => <Tag color={r.match ? (r.status === "none" ? "green" : HOLAT[r.status]?.c) : "red"} style={{ marginRight: 0, whiteSpace: "nowrap" }} data-testid="ai-list-status">{r.match ? (r.status === "none" ? "✓ " + t("aiImgFoundManual", "выбран") : HOLAT[r.status]?.l()) : HOLAT.none.l()}</Tag>
  // tog'a #3485: topilgan tovarning TO'LIQ ma'lumoti — nom, artikul, shtrix-kod, qoldiq, narx (yetkazuvchida qoldiq/kod ko'rsatilmaydi)
  const tovarInfo = (p: Tovar) => (
    <div className="ai-list-info" data-testid="ai-list-info">
      <b>{p.name}</b>
      <span>
        {p.article ? <>{t("article", "Артикул")}: <b>{p.article}</b></> : null}
        {!supRejim && p.barCode ? <> · {t("barCode", "Штрих-код")}: {p.barCode}</> : null}
      </span>
      <span>
        {!supRejim && <>{t("aiImgStock", "Остаток")}: <b style={{ color: Number(p.stock || 0) > 0 ? "#16a34a" : "#dc2626" }}>{Number(p.stock || 0)}</b> · </>}
        {t("aiImgPrice", "Цена")}: <b>{Number(p.price) > 0 ? "$" + Number(p.price) : "—"}</b>   {/* #3503: narx kiritilmagan bo'lsa «$0» emas */}
        {supRejim && <> · <i>{t("aiImgFromSup", "из каталога поставщика")}</i></>}
      </span>
    </div>
  )
  const tovarCell = (r: Qator) => (
    <div style={{ minWidth: narrow ? 0 : 280, display: "flex", flexDirection: "column", gap: 4 }}>
      {r.match && tovarInfo(r.match)}
      {r.candidates.length > 1 || (r.candidates.length === 1 && !r.match) ? (
        <Select size={narrow ? "middle" : "small"} value={r.match?.productId} placeholder={t("aiImgChoose", "Tanlang")} style={{ width: "100%" }} data-testid="ai-list-cand"
          onChange={(v) => yangila(r.key, { match: r.candidates.find((c) => c.productId === v) || null, status: "ok" })}
          options={r.candidates.map((c) => ({ value: c.productId, label: `${c.name}${c.article ? " · " + c.article : ""} (${Math.round((c.score || 0) * 100)}%)` }))} />
      ) : null}
      <TovarQidir supplier={supRejim} onPick={(p) => yangila(r.key, { status: "ok", match: p, candidates: r.candidates.some((c) => c.productId === p.productId) ? r.candidates : [p, ...r.candidates] })} />
    </div>
  )
  const qtyCell = (r: Qator) => <InputNumber size={narrow ? "middle" : "small"} min={0} value={r.qty} style={{ width: narrow ? "100%" : 84 }} onChange={(x) => ikkalasi((p) => p.map((q) => (q.key === r.key ? { ...q, qty: Number(x) || 0 } : q)))} />
  const priceCell = (r: Qator) => <InputNumber size={narrow ? "middle" : "small"} min={0} value={r.price ?? undefined} style={{ width: narrow ? "100%" : 100 }} onChange={(x) => ikkalasi((p) => p.map((q) => (q.key === r.key ? { ...q, price: x == null ? null : Number(x) } : q)))} />
  const delBtn = (r: Qator) => <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => ikkalasi((p) => p.filter((x) => x.key !== r.key))} />
  const otkaz = () => {
    if (!target) return
    if (!tayyor.length) return message.warning(t("aiImgPickFirst", "Avval tovarlarni tanlang"))
    setAiPrefill(target, tayyor.map((r) => ({
      productId: supRejim ? 0 : r.match!.productId, supplierProductId: supRejim ? r.match!.supplierProductId ?? r.match!.productId : undefined,
      name: r.match!.name, article: r.match!.article || "", barCode: r.match!.barCode || "",
      stock: Number(r.match!.stock || 0), price: Number(r.match!.price || 0), costPriceUSD: Number(r.match!.costPriceUSD || 0), discount: Number(r.match!.discount || 0), qty: r.qty, paperPrice: r.price ?? null,
    })))
    onClose()
    onDone?.()   // AI oynasi yopiladi — forma ochiq ko'rinadi
    history.push(YOL[target])
  }
  const holatSarlavha = (
    <Tooltip title={t("aiImgFoundTip", "Нашёл ли AI эту строку с бумаги среди товаров в базе: ✓ найден — товар выбран сам; ? проверьте — похожий, проверьте; ✗ нет в базе — выберите товар сами через поиск.")}>
      <span style={{ whiteSpace: "nowrap" }}>{t("aiImgFoundCol", "Найден в базе?")} ⓘ</span>
    </Tooltip>
  )
  return (
    <Modal visible width="min(1100px, 100vw)" zIndex={1010} onCancel={onClose} title={"📷 " + t("aiImgTitle", "Qog'ozdagi ro'yxat — AI o'qidi")} data-testid="ai-list-modal"
      footer={[
        <span key="n" style={{ marginRight: 12, opacity: 0.8 }}>{t("aiImgReady", "Formaga o'tadi")}: <b>{tayyor.length}</b> / {rows.length}</span>,
        <Button key="c" onClick={onClose}>{t("cancel", "Отмена")}</Button>,
        <Button key="o" type="primary" onClick={otkaz} disabled={!tayyor.length || !target} data-testid="ai-list-go">{t("aiImgGo", "Formaga o'tkazish")} →</Button>,
      ]}>
      <Alert type="info" showIcon style={{ marginBottom: 10 }}
        message={t("aiImgHelp", "Har qatorni tekshiring: tovar to'g'ri tanlanganmi, soni va narxi. Sariq — tekshiring, qizil — tovarni o'zingiz tanlang. Hech narsa hali saqlanmagan.")}
        description={(data.counterparty ? `${t("aiImgCt", "Hujjatda")}: ${data.counterparty}. ` : "") + (data.currency ? `${t("aiImgCur", "Valyuta")}: ${data.currency}.` : "")} />
      {ruxsat.length === 0 ? (
        <Alert type="warning" showIcon style={{ marginBottom: 10 }} data-testid="ai-list-noaccess" message={t("aiImgNoTargets", "Нет доступа ни к одному разделу (Закупка, Продажа, Заказ) — список можно только посмотреть. Доступ выдаёт администратор.")} />
      ) : (
        <Space wrap style={{ marginBottom: 10 }}>
          <b>{t("aiImgTarget", "Qaysi bo'limga")}:</b>
          <Radio.Group value={target} onChange={(e) => setTarget(e.target.value)} buttonStyle="solid" data-testid="ai-list-target">
            {ruxsat.map((b) => <Radio.Button key={b.v} value={b.v}>{b.v === "order" && data.hasSupplier ? t("aiImgOrderClient", "Заказ клиента") : b.label()}</Radio.Button>)}
          </Radio.Group>
          <span style={{ fontSize: 12, opacity: 0.75 }}>{target === "purchase" ? t("aiImgPriceP", "Zakupkada qog'ozdagi narx qo'yiladi") : supRejim ? t("aiImgSupHint", "Товары из каталога поставщика — заказ уйдёт поставщику") : t("aiImgPriceS", "Narx katalogdan olinadi")}</span>
        </Space>
      )}
      {narrow ? (
        <div className="ai-list-cards">
          {rows.map((r) => (
            <div key={r.key} className={`ai-list-card ${r.match ? "" : "none"}`} data-testid="ai-list-card">
              <div className="ai-list-card-head">
                <span className="ai-list-n">{r.key}</span>
                <div style={{ flex: 1, minWidth: 0 }}><b>{r.readName || "—"}</b>{r.readArticle && r.readArticle !== r.readName ? <div className="ai-list-art">{r.readArticle}</div> : null}</div>
                {holatTag(r)}{delBtn(r)}
              </div>
              {tovarCell(r)}
              <div className="ai-list-card-nums">
                <label><span>{t("quantity", "Soni")}</span>{qtyCell(r)}</label>
                <label><span>{t("aiImgPaperPrice", "Narx (qog'ozda)")}</span>{priceCell(r)}</label>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <StickyTable size="small" rowKey="key" pagination={false} dataSource={rows} scroll={{ x: "max-content", y: 420 }}
        columns={[
          { title: "№", dataIndex: "key", width: 44 },
          { title: t("aiImgPaper", "Qog'ozda"), key: "read", render: (_: any, r: Qator) => <div style={{ minWidth: 120 }}><b>{r.readName || "—"}</b>{r.readArticle && r.readArticle !== r.readName ? <div style={{ fontSize: 11, opacity: 0.7 }}>{r.readArticle}</div> : null}</div> },
          { title: holatSarlavha, dataIndex: "status", width: 140, render: (_: string, r: Qator) => holatTag(r) },   // #3503: sarlavha kesilmasin
          { title: t("product", "Tovar (tizimda)"), key: "match", render: (_: any, r: Qator) => tovarCell(r) },
          { title: t("quantity", "Soni"), dataIndex: "qty", render: (_: number, r: Qator) => qtyCell(r) },
          { title: t("aiImgPaperPrice", "Narx (qog'ozda)"), dataIndex: "price", render: (_: number | null, r: Qator) => priceCell(r) },
          { title: "", key: "del", width: 40, render: (_: any, r: Qator) => delBtn(r) },
        ]} />
      )}
    </Modal>
  )
}
